/**
 * Per-role connected-app capability ceilings — shared frontend constants
 * (Round-16, PLAN-connected-apps-governance-20260816 §5 + §6).
 *
 * KEEP IN LOCKSTEP with functions/src/rbac/roleAppAccess.ts (families, entry
 * shape) and functions/src/composio/accessPolicy.ts (tier ⇄ profile
 * translation). tests/teamroles/roleAppAccessEditor.test.ts byte-compares
 * the APP_FAMILIES literal against the backend file and fails on drift —
 * the same posture as the permission-catalog sync gate.
 */

// ── Entry shapes (mirror of the roles.ts write-path contract) ────────────────

export type AccessTier = 'full' | 'read-write' | 'read-only';

export type CapabilityClass = 'read' | 'create' | 'send' | 'delete' | 'admin';

/**
 * `read` is always true — a connected app can always be read; removing read
 * access is what disconnect is for (accessPolicy.AccessProfile mirror).
 */
export interface AccessProfile {
  read: true;
  create: boolean;
  send: boolean;
  delete: boolean;
  admin: boolean;
}

/** One appAccess value: a preset name or an explicit five-class profile. */
export type AppAccessEntry = AccessTier | AccessProfile;

/**
 * The role doc field: family id (or `app:<slug>` override) → entry.
 * `app:<slug>` beats the family entry server-side (more specific wins).
 */
export type RoleAppAccess = Record<string, AppAccessEntry>;

// ── App families (§4.1) — keep in lockstep with functions/src/rbac/roleAppAccess.ts ──

export const APP_FAMILIES: Record<string, { label: string; slugs: string[] }> = {
  email: { label: 'Email', slugs: ['gmail', 'outlook'] },
  calendar: { label: 'Calendar & scheduling', slugs: ['googlecalendar', 'calendly', 'zoom'] },
  storage: {
    label: 'Files & storage',
    slugs: ['googledrive', 'googledocs', 'googlesheets', 'googleslides', 'dropbox', 'box', 'one_drive', 'share_point', 'sharepoint'],
  },
  docs: { label: 'Docs & notes', slugs: ['notion', 'airtable'] },
  messaging: { label: 'Messaging & chat', slugs: ['slack', 'microsoft_teams', 'whatsapp', 'intercom'] },
  crm: { label: 'CRM & clients', slugs: ['hubspot', 'salesforce', 'zendesk', 'googlecontacts'] },
  finance: { label: 'Finance & billing', slugs: ['quickbooks', 'stripe', 'xero', 'freshbooks'] },
  signatures: { label: 'Signatures & legal', slugs: ['docusign', 'dropbox_sign', 'pandadoc'] },
  mail: { label: 'Mail & postage', slugs: ['postgrid'] },
  projects: { label: 'Projects & tasks', slugs: ['jira', 'asana', 'monday', 'clickup', 'trello', 'todoist', 'basecamp', 'linear'] },
  marketing: { label: 'Marketing & social', slugs: ['mailchimp', 'linkedin', 'typeform', 'youtube', 'facebook', 'instagram', 'pinterest', 'reddit'] },
  developer: { label: 'Developer & ops', slugs: ['github', 'gitlab', 'miro', 'figma', 'canva'] },
  other: { label: 'Everything else', slugs: [] },
};

/** Family ids in render order (object-literal order is the display order). */
export const APP_FAMILY_IDS = Object.keys(APP_FAMILIES);

// ── Capability classes (§3.1) ────────────────────────────────────────────────

export const CAPABILITY_CLASSES: CapabilityClass[] = [
  'read', 'create', 'send', 'delete', 'admin',
];

export const CAPABILITY_CLASS_LABELS: Record<CapabilityClass, string> = {
  read: 'Read',
  create: 'Create & edit',
  send: 'Send & outbound',
  delete: 'Delete',
  admin: 'Admin & settings',
};

/** One-line per-class summaries for the fine-tune rows. */
export const CAPABILITY_CLASS_SUMMARIES: Record<CapabilityClass, string> = {
  read: 'look things up and search',
  create: 'create, edit, upload, organize',
  send: 'send messages, post, or sign — reaches people outside',
  delete: 'delete or trash content',
  admin: 'sharing, permissions, members, settings',
};

// ── Tier ⇄ profile translation (lossless — accessPolicy.ts mirror, §3.2) ─────

export function isAccessTier(v: unknown): v is AccessTier {
  return v === 'full' || v === 'read-write' || v === 'read-only';
}

export function profileFromTier(tier: AccessTier): AccessProfile {
  if (tier === 'read-only') {
    return { read: true, create: false, send: false, delete: false, admin: false };
  }
  if (tier === 'read-write') {
    return { read: true, create: true, send: true, delete: false, admin: false };
  }
  return { read: true, create: true, send: true, delete: true, admin: true };
}

/** The preset a profile matches, or null for a custom mix. */
export function tierFromProfile(p: AccessProfile): AccessTier | null {
  if (p.create && p.send && p.delete && p.admin) return 'full';
  if (p.create && p.send && !p.delete && !p.admin) return 'read-write';
  if (!p.create && !p.send && !p.delete && !p.admin) return 'read-only';
  return null;
}

/** Normalize a stored entry (preset name or profile) to its profile. */
export function entryToProfile(entry: AppAccessEntry): AccessProfile {
  return typeof entry === 'string' ? profileFromTier(entry) : entry;
}

// ── Presets (the three shipped tiers, in ceiling order) ──────────────────────

export const PRESET_OPTIONS: Array<{ tier: AccessTier; label: string }> = [
  { tier: 'full', label: 'Everything' },
  { tier: 'read-write', label: 'Standard (no delete, no settings)' },
  { tier: 'read-only', label: 'Read-only' },
];
